import type { StoreApi } from 'zustand';

import type { ResearchTutorialActions } from './researchTutorialContext';

import { RESEARCH_TUTORIAL_SEGMENTS, zoneKey } from '@/constants/researchTutorial';
import type { TourCalloutHandles } from '@/hooks/useTourCallout';
import type { ResearchTutorialStore } from '@/services/stores/researchTutorialStore';
import {
  trackResearchTutorialCompleted,
  trackResearchTutorialSkipped,
  trackResearchTutorialStarted,
} from '@/util/analyticsEvents';

/**
 * Builds the tutorial's actions once, over its store and the latest callout
 * handles, so every action keeps one identity for the life of the layout.
 *
 * The callout is resolved on Skip, or once the tutorial's question has been
 * accepted. The results steps that follow are a bonus, so a reader who leaves
 * while the search runs is not shown the whole tour again. A refused send
 * leaves it unresolved and returns the reader to composing.
 *
 * "Started" is reported, and the callout marked shown, when the first segment
 * reaches the screen rather than when the tutorial is requested, so a reader
 * who leaves before the tour appears is not counted.
 *
 * `retryStart` lets the provider begin a tutorial that was requested before
 * the callouts query had loaded.
 */
export function createResearchTutorialActions(
  store: StoreApi<ResearchTutorialStore>,
  callout: { current: TourCalloutHandles }
): { actions: ResearchTutorialActions; retryStart: () => void } {
  const zoneActions: Record<string, (() => void) | undefined> = {};

  let isStartRequested = false;

  let hasMarkedSeen = false;

  const markSeenOnce = () => {
    if (hasMarkedSeen) {
      return;
    }

    hasMarkedSeen = true;

    callout.current.markSeen();
  };

  const retryStart = () => {
    if (isStartRequested && callout.current.shouldShow) {
      store.getState().begin();
    }
  };

  const actions: ResearchTutorialActions = {
    requestStart: () => {
      isStartRequested = true;

      retryStart();
    },

    cancelStart: () => {
      isStartRequested = false;
    },

    sendStarted: (conversationId) => {
      store.getState().sendStarted(conversationId);
    },

    sendSettled: (conversationId, accepted) => {
      if (store.getState().conversationId !== conversationId) {
        return;
      }

      if (accepted) {
        markSeenOnce();
      } else {
        store.getState().sendFailed(conversationId);
      }
    },

    advance: ({ segment, zone, isLastStep, handleNext, handleStop }) => {
      if (RESEARCH_TUTORIAL_SEGMENTS[segment].steps[zone]?.actionLabel) {
        zoneActions[zoneKey(segment, zone)]?.();

        // The action may already have moved the tutorial on, as a send does.
        if (store.getState().phase === segment) {
          handleStop();

          store.getState().finishSegment(segment);
        }

        return;
      }

      if (!isLastStep) {
        handleNext();

        return;
      }

      handleStop();

      if (store.getState().finishSegment(segment) === 'done') {
        trackResearchTutorialCompleted();
      }
    },

    skip: (segment, zone, handleStop) => {
      handleStop();

      store.getState().skip();

      markSeenOnce();

      trackResearchTutorialSkipped({ segment, step: zone });
    },

    setZoneAction: (key, action) => {
      zoneActions[key] = action;
    },

    segmentShown: (segment) => {
      if (store.getState().segmentStarted(segment)) {
        callout.current.markShown();

        trackResearchTutorialStarted();
      }
    },
  };

  return { actions, retryStart };
}
